import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import type { ISearchProduct } from '@boss/types/b2b-b2c';
import { Section, Skeleton } from '@boss/ui';

import { useAccountCampaignInfo, useParticipatingProducts, useProductsById } from '../../client-queries';
import { ProductCardWrapper } from '../../components';
import { useDeepLinkEligibilityGuard, useProductMap } from '../../hooks';

/** campaignId from the [campaignId] path segment (surfaced as query by Next). */
const readCampaignId = (query: ReturnType<typeof useRouter>['query']): string =>
  typeof query.campaignId === 'string' ? query.campaignId : '';

/** Comma-separated `?categories=` param → clean list (optional param). */
const readCategories = (query: ReturnType<typeof useRouter>['query']): string[] => {
  const raw = query.categories;
  const parts = Array.isArray(raw) ? raw.join(',').split(',') : (raw ?? '').split(',');

  return parts.map(category => category.trim()).filter(Boolean);
};

/** PDP-existence filter: keep groups the product API returned data for, in API order (AC5). */
const filterByPdp = (groups: { productgroupid: number }[], products: ISearchProduct[]): ISearchProduct[] => {
  const productById = new Map(products.map(product => [product.id, product]));

  return groups.flatMap(group => {
    const product = productById.get(String(group.productgroupid));

    return product ? [product] : [];
  });
};

const GRID_CLASS = 'my-5 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4';

const GridSkeleton = () => (
  <div className={GRID_CLASS}>
    {[...Array(4)].map((_, index) => (
      <Skeleton className="h-100" key={`participating-skeleton-${index}`} />
    ))}
  </div>
);

const EmptyState = () => {
  const { t } = useTranslation('common');

  return (
    <p className="py-10 text-center">
      {t('campaign.participatingProducts.empty', { defaultValue: 'Geen producten beschikbaar.' })}
    </p>
  );
};

const ProductGrid = ({ products }: { products: ISearchProduct[] }) => {
  const { mapProductComponent } = useProductMap();

  return (
    <div className={GRID_CLASS}>
      {products.map(product => (
        // AC3: tile design unchanged — ProductCardWrapper is the shared tile
        <ProductCardWrapper key={`participating-product-card-${product.id}`} {...mapProductComponent(product)} />
      ))}
    </div>
  );
};

/**
 * Participating-products grid (ticket 45171, analyst design:
 * /actie/<CampaignId>?categories=<url-safe cat1>,<url-safe cat2>).
 *
 * Loads groups from /v2/campaign/products (via /api/campaign/products; no
 * categories = all products for the campaign), then PDP render data via the
 * shared product-by-ids query. Response order is preserved as display order (AC1).
 */
// fallow-ignore-next-line complexity -- two chained queries (groups → PDP data) with loading/empty/grid states; logic extracted into helpers below.
const ParticipatingProductsGrid = () => {
  const { query, locale } = useRouter();

  // Ticket 45173: fire /discountinfos type=4 on every campaign-page load
  // (data only; tile filtering is a later ticket).
  useAccountCampaignInfo(locale as string);

  const campaignId = readCampaignId(query);
  const categories = readCategories(query);

  const { data: productGroups, isLoading: groupsLoading } = useParticipatingProducts(campaignId, categories);

  const groupIds = (productGroups ?? []).map(group => String(group.productgroupid));
  const { data: products, isLoading: productsLoading } = useProductsById(groupIds);

  // [45176] Deep-link gate: the guard redirects ineligible deep-links (homepage
  // for anonymous, account page for logged-in); paint nothing while the guard's
  // profile/eligibility checks resolve or the redirect is in flight.
  const { pending, blocked } = useDeepLinkEligibilityGuard({ campaignId, categories });

  if (pending || blocked) {
    return null;
  }

  if (groupsLoading || (groupIds.length > 0 && productsLoading)) {
    return <GridSkeleton />;
  }

  const visibleProducts = filterByPdp(productGroups ?? [], products ?? []);

  if (!visibleProducts.length) {
    return <EmptyState />;
  }

  return <ProductGrid products={visibleProducts} />;
};

type Props = {
  preview: boolean;
};

export const ParticipatingProductsPage = ({ preview }: Props) => {
  const { t } = useTranslation('common');

  return (
    <Section
      content={<ParticipatingProductsGrid />}
      title={
        // h1 gets the base typography from the tailwind preset (getTailwindBase),
        // same as every other page title rendered inside Section.
        <h1 className="mb-5 lg:mb-12">
          {t('campaign.participatingProducts.title', { defaultValue: 'Participerende producten' })}
        </h1>
      }
    />
  );
};

export default ParticipatingProductsPage;
